import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Upload, RefreshCw } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

/**
 * HUPALUPA Store · Kanal bazlı satışlar (/r/hupalupa-kanal-satislari).
 *
 * Website + pazaryerleri (Hepsiburada, Trendyol, Amazon, Beymen, Boyner, N11) günlük
 * sipariş ve KDV hariç satış. Kaynak mağazanın "sales-by-channel" CSV dışa aktarımı —
 * Glowytics'te bu verinin canlı bağlantısı yok, sayfadaki "CSV yükle" ile marka
 * şemasına yazılır. Uç: glowytics-backend/src/routes/hupalupaChannelSales.js
 * Sadece HUPALUPA Store (App.js allowBrandIds). Panel içinde, sidebar'lı.
 */

const API_BASE = (() => {
  const raw = (process.env.REACT_APP_API_URL || 'https://api.glowytics.com').trim();
  return raw.replace(/\/+$/, '').replace(/\/api$/, '');
})();

const C = {
  ground: '#F6F8FF', card: '#FFFFFF', line: '#E5E7EB', hair: '#F3F4F6',
  ink: '#111827', ink2: '#374151', muted: '#6B7280', faint: '#9CA3AF',
  accent: '#436AEB', accentSoft: '#EEF2FE', up: '#047857', down: '#B91C1C'
};
// Kanal renkleri sabit — dosyaya yeni kanal gelirse sıradaki yedek renk verilir.
const KANAL_RENK = {
  Website: '#436AEB', Hepsiburada: '#F28C28', Trendyol: '#E0457B', Beymen: '#1F2937',
  Amazon: '#14A38B', Boyner: '#8B5CF6', N11: '#9CA3AF'
};
const YEDEK_RENK = ['#0EA5E9', '#CA8A04', '#65A30D', '#DC2626'];

const FONT = '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
const nf = (n) => Math.round(Number(n || 0)).toLocaleString('tr-TR');
const tl = (n) => `₺${nf(n)}`;
const tlShort = (n) => {
  const v = Number(n || 0);
  if (Math.abs(v) >= 1e6) return `₺${(v / 1e6).toLocaleString('tr-TR', { maximumFractionDigits: 1 })}M`;
  if (Math.abs(v) >= 1e4) return `₺${(v / 1e3).toLocaleString('tr-TR', { maximumFractionDigits: 0 })}B`;
  return tl(v);
};
const pct = (x, d = 1) => `%${(Number(x || 0) * 100).toLocaleString('tr-TR', { maximumFractionDigits: d, minimumFractionDigits: d })}`;
const pctIsaret = (x) => `${x >= 0 ? '+' : '−'}${pct(Math.abs(x))}`;
const AY = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
const AY_TAM = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
const gunEtiket = (iso) => `${Number(iso.slice(8, 10))} ${AY[Number(iso.slice(5, 7)) - 1]}`;
const ayGunSayisi = (ay) => new Date(Date.UTC(+ay.slice(0, 4), +ay.slice(5, 7), 0)).getUTCDate();

const kart = { background: C.card, border: `1px solid ${C.line}`, borderRadius: 12, padding: 20, minWidth: 0 };
const baslik = { fontSize: 15, fontWeight: 600, color: C.ink, margin: 0 };
const not = { fontSize: 12, color: C.muted, margin: '4px 0 14px' };

function Kutucuk({ etiket, deger, alt, degisim, kiyas }) {
  return (
    <div style={{ ...kart, display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={{ fontSize: 12, color: C.muted }}>{etiket}</span>
      <span style={{ fontSize: 26, fontWeight: 600, color: C.ink, fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.01em' }}>{deger}</span>
      {degisim != null && Number.isFinite(degisim) ? (
        <span style={{ fontSize: 12, fontWeight: 600, color: degisim >= 0 ? C.up : C.down }}>
          {pctIsaret(degisim)} <span style={{ color: C.faint, fontWeight: 400 }}>{kiyas}</span>
        </span>
      ) : alt && <span style={{ fontSize: 12, color: C.faint }}>{alt}</span>}
    </div>
  );
}

function AylikIpucu({ active, payload, label, kanallar, kismi }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 10, boxShadow: '0 6px 20px rgba(17,24,39,.08)', padding: '8px 12px', fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>
      <div style={{ color: C.muted, marginBottom: 4 }}>
        {AY_TAM[+label.slice(5, 7) - 1]} {label.slice(0, 4)}{kismi[label] ? ` (1–${kismi[label]})` : ''}
        {!d.eksik && <> · <b style={{ color: C.ink }}>{tl(d.toplam)}</b></>}
      </div>
      {d.eksik && <div style={{ color: C.faint }}>Bu ay için veri yok</div>}
      {kanallar.filter((k) => d[k.platform] > 0).map((k) => (
        <div key={k.platform} style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
          <span><i style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: k.renk, marginRight: 6 }} />{k.platform}</span>
          <span>{tl(d[k.platform])}</span>
        </div>
      ))}
    </div>
  );
}

const MIN_KIYAS_GUN = 5;

const SUTUNLAR = [
  { k: 'platform', ad: 'Kanal', sol: true },
  { k: 'net_satis', ad: 'Net satış', f: tl },
  { k: 'yoy', ad: 'Geçen yıla göre', yoy: true },
  { k: 'pay', ad: 'Satış payı' },
  { k: 'siparis', ad: 'Sipariş', f: nf },
  { k: 'siparis_pay', ad: 'Sipariş payı', f: (v) => pct(v) },
  { k: 'sepet', ad: 'Ort. sepet', f: tl },
  { k: 'gun', ad: 'Aktif gün', f: nf },
  { k: 'ilk', ad: 'İlk satış', f: gunEtiket },
];

export default function HupalupaKanalSatis() {
  const brandId = typeof window !== 'undefined' ? localStorage.getItem('currentBrandId') : null;
  const headers = useMemo(
    () => ({ 'Content-Type': 'application/json', ...(brandId ? { 'X-Brand-ID': brandId } : {}) }),
    [brandId]
  );
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hata, setHata] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [sirala, setSirala] = useState({ k: 'net_satis', yon: -1 });
  const [yil, setYil] = useState(null); // null = verideki son yıl, 'tumu' = hepsi
  const dosyaRef = useRef(null);

  const getir = useCallback(async () => {
    setLoading(true); setHata(null);
    try {
      const res = await fetch(`${API_BASE}/api/hupalupa/channel-sales`, { credentials: 'include', headers });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Veri yüklenemedi');
      setData(json);
    } catch (e) {
      setHata(e.message);
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => { getir(); }, [getir]);

  const dosyaSecildi = async (ev) => {
    const file = ev.target.files?.[0];
    ev.target.value = '';
    if (!file) return;
    setYukleniyor(true);
    try {
      const csv = await file.text();
      const res = await fetch(`${API_BASE}/api/hupalupa/channel-sales/upload`, {
        method: 'POST', credentials: 'include', headers, body: JSON.stringify({ csv, fileName: file.name })
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Dosya yüklenemedi');
      toast.success(`${nf(json.satir)} satır yüklendi`);
      getir();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setYukleniyor(false);
    }
  };

  const yillar = useMemo(() => [...new Set((data?.rows || []).map((r) => r.tarih.slice(0, 4)))].sort(), [data]);
  const yilSecili = yil || yillar[yillar.length - 1] || null;

  const o = useMemo(() => {
    const tum = data?.rows || [];
    const rows = yilSecili === 'tumu' ? tum : tum.filter((r) => r.tarih.startsWith(yilSecili || ''));
    if (!rows.length) return null;
    const topla = (rs) => rs.reduce((t, r) => { t.net += r.net_satis; t.sip += r.siparis; t.toplam += r.toplam; return t; }, { net: 0, sip: 0, toplam: 0 });
    const { net, sip, toplam } = topla(rows);
    const ilkTarih = rows[0].tarih;
    const sonTarih = rows[rows.length - 1].tarih;

    // Geçen yılın AYNI günleri (ör. 1 Oca–17 Eki) — yalnız tek yıl seçiliyken.
    let gecen = null;
    if (yilSecili !== 'tumu') {
      const gy = String(+yilSecili - 1);
      const bas = gy + ilkTarih.slice(4), bit = gy + sonTarih.slice(4);
      const gRows = tum.filter((r) => r.tarih >= bas && r.tarih <= bit);
      if (gRows.length) {
        const kanal = new Map();
        gRows.forEach((r) => {
          const g = kanal.get(r.platform) || { net: 0, gun: 0 };
          g.net += r.net_satis; g.gun += 1;
          kanal.set(r.platform, g);
        });
        gecen = { ...topla(gRows), kanal, yil: gy, bas, bit };
      }
    }

    const kMap = new Map();
    rows.forEach((r) => {
      const k = kMap.get(r.platform) || { platform: r.platform, net_satis: 0, siparis: 0, gun: 0, ilk: r.tarih };
      k.net_satis += r.net_satis; k.siparis += r.siparis; k.gun += 1;
      if (r.tarih < k.ilk) k.ilk = r.tarih;
      kMap.set(r.platform, k);
    });
    let yedek = 0;
    const kanallar = [...kMap.values()]
      .map((k) => {
        const g = gecen?.kanal.get(k.platform);
        return {
          ...k, pay: k.net_satis / net, siparis_pay: k.siparis / sip, sepet: k.siparis ? k.net_satis / k.siparis : 0,
          // Geçen yıl 5 günden az satışı olan kanal "yeni" sayılır — tek günlük tabana göre yüzde anlamsız.
          yoy: g && g.gun >= MIN_KIYAS_GUN && g.net > 0 ? k.net_satis / g.net - 1 : null
        };
      })
      .sort((a, b) => b.net_satis - a.net_satis)
      .map((k) => ({ ...k, renk: KANAL_RENK[k.platform] || YEDEK_RENK[yedek++ % YEDEK_RENK.length] }));

    const aMap = new Map();
    rows.forEach((r) => {
      const ay = r.tarih.slice(0, 7);
      const a = aMap.get(ay) || { ay, toplam: 0, siparis: 0, sonGun: r.tarih };
      a[r.platform] = (a[r.platform] || 0) + r.net_satis;
      a.toplam += r.net_satis; a.siparis += r.siparis;
      if (r.tarih > a.sonGun) a.sonGun = r.tarih;
      aMap.set(ay, a);
    });
    // Ay ekseni kesintisiz: veri olmayan ay boş sütun olarak kalır (ör. Kas–Ara 2025).
    const aylar = [];
    for (let y = +ilkTarih.slice(0, 4), m = +ilkTarih.slice(5, 7); `${y}-${String(m).padStart(2, '0')}` <= sonTarih.slice(0, 7); m === 12 ? (y += 1, m = 1) : (m += 1)) {
      const ay = `${y}-${String(m).padStart(2, '0')}`;
      aylar.push(aMap.get(ay) || { ay, toplam: 0, siparis: 0, eksik: true });
    }
    // Ay yarımsa (son günü ay sonu değil ve ardından veri gelmiyorsa) kıyasta kullanılmaz.
    const tumAylar = new Set(tum.map((r) => r.tarih.slice(0, 7)));
    const sonrakiAy = (ay) => { const y = +ay.slice(0, 4), m = +ay.slice(5, 7); return m === 12 ? `${y + 1}-01` : `${y}-${String(m + 1).padStart(2, '0')}`; };
    const kismi = {};
    aylar.forEach((a) => {
      if (a.eksik) return;
      const gun = +a.sonGun.slice(8, 10);
      if (gun < ayGunSayisi(a.ay) && !tumAylar.has(sonrakiAy(a.ay))) kismi[a.ay] = gun;
    });
    const eksikAylar = aylar.filter((a) => a.eksik).map((a) => a.ay);
    const tamAylar = aylar.filter((a) => !a.eksik && !kismi[a.ay]);
    const enIyi = tamAylar.reduce((b, a) => (!b || a.toplam > b.toplam ? a : b), null);
    const oncekiAy = enIyi && aylar[aylar.indexOf(enIyi) - 1];
    const onceki = oncekiAy && !oncekiAy.eksik && !kismi[oncekiAy.ay] ? oncekiAy : null;
    const enCokSiparis = [...kanallar].sort((a, b) => b.siparis - a.siparis)[0];

    return { net, sip, toplam, ilkTarih, sonTarih, kanallar, aylar, kismi, eksikAylar, enIyi, onceki, enCokSiparis, gecen };
  }, [data, yilSecili]);

  const sirali = useMemo(() => {
    if (!o) return [];
    const { k, yon } = sirala;
    return [...o.kanallar].sort((a, b) => yon * (typeof a[k] === 'string' ? a[k].localeCompare(b[k], 'tr') : a[k] - b[k]));
  }, [o, sirala]);

  const sutunlar = o?.gecen ? SUTUNLAR : SUTUNLAR.filter((s) => !s.yoy);
  const donem = o ? `${gunEtiket(o.ilkTarih)}${o.ilkTarih.slice(0, 4) !== o.sonTarih.slice(0, 4) ? ` ${o.ilkTarih.slice(0, 4)}` : ''} – ${gunEtiket(o.sonTarih)} ${o.sonTarih.slice(0, 4)}` : '';
  const son = data?.sonYukleme;
  const ayEtiket = (ay) => `${AY[+ay.slice(5, 7) - 1]}${yilSecili === 'tumu' ? ` '${ay.slice(2, 4)}` : ''}${o?.kismi[ay] ? '*' : ''}`;
  const ayYil = (ay) => `${AY_TAM[+ay.slice(5, 7) - 1]} ${ay.slice(0, 4)}`;
  const gecenDonem = o?.gecen ? `${o.gecen.yil} aynı döneme göre` : '';

  return (
    <div lang="tr" style={{ background: C.ground, minHeight: '100%', fontFamily: FONT, color: C.ink, padding: '20px 16px 40px' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 20 }}>
        <header style={{ display: 'flex', flexWrap: 'wrap', gap: 16, justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ minWidth: 0, flex: '1 1 420px' }}>
            <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0, letterSpacing: '-0.01em' }}>Kanal Bazlı Satışlar</h1>
            {o && (
              <p style={{ fontSize: 14, color: C.ink2, margin: '6px 0 0', lineHeight: 1.55, maxWidth: 760 }}>
                {donem}, KDV hariç.
                {o.gecen && ` Net satış ${o.gecen.yil}'in aynı dönemine göre ${pctIsaret(o.net / o.gecen.net - 1)}.`}
                {o.enIyi && ` En güçlü ay ${ayYil(o.enIyi.ay)} (${tlShort(o.enIyi.toplam)})${o.onceki ? `, bir önceki aya göre ${pctIsaret(o.enIyi.toplam / o.onceki.toplam - 1)}` : ''}.`}
                {' '}Net satışın {pct(o.kanallar[0].pay)} kadarı {o.kanallar[0].platform} kanalından geliyor;
                en çok sipariş {o.enCokSiparis.platform}'da ({pct(o.enCokSiparis.siparis_pay)}), ortalama sepeti {tl(o.enCokSiparis.sepet)}.
              </p>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              {yillar.length > 1 && (
                <div role="group" aria-label="Yıl" style={{ display: 'inline-flex', border: `1px solid ${C.line}`, borderRadius: 8, overflow: 'hidden', background: C.card }}>
                  {[...yillar, 'tumu'].map((y) => (
                    <button key={y} type="button" aria-pressed={yilSecili === y} onClick={() => setYil(y)}
                      style={{ border: 0, padding: '8px 12px', fontSize: 13, cursor: 'pointer', fontWeight: yilSecili === y ? 600 : 400, background: yilSecili === y ? C.accentSoft : 'transparent', color: yilSecili === y ? C.accent : C.ink2 }}>
                      {y === 'tumu' ? 'Tümü' : y}
                    </button>
                  ))}
                </div>
              )}
              <button type="button" onClick={getir} disabled={loading} title="Yenile"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, border: `1px solid ${C.line}`, background: C.card, color: C.ink2, borderRadius: 8, padding: '8px 10px', fontSize: 13, cursor: 'pointer' }}>
                <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
              </button>
              <button type="button" onClick={() => dosyaRef.current?.click()} disabled={yukleniyor}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, border: 0, background: C.accent, color: '#fff', borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: yukleniyor ? 0.6 : 1 }}>
                <Upload size={14} /> {yukleniyor ? 'Yükleniyor…' : 'CSV yükle'}
              </button>
              <input ref={dosyaRef} id="kanal-csv" type="file" accept=".csv,text/csv" hidden onChange={dosyaSecildi} />
            </div>
            {son && (
              <span style={{ fontSize: 12, color: C.faint, textAlign: 'right' }}>
                Son yükleme {new Date(son.created_at).toLocaleString('tr-TR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                {' · '}{gunEtiket(son.ilk_tarih)}–{gunEtiket(son.son_tarih)}
              </span>
            )}
          </div>
        </header>

        {hata && <div style={{ ...kart, color: C.down, fontSize: 14 }}>{hata}</div>}
        {!hata && !loading && !o && (
          <div style={{ ...kart, fontSize: 14, color: C.ink2, lineHeight: 1.6 }}>
            Henüz veri yok. Mağaza panelinden "sales-by-channel" raporunu CSV olarak indirip <b>CSV yükle</b> ile ekleyin.
            Beklenen sütunlar: Platform; Tarih; Sipariş Sayısı; Satış Toplamı KDV Hariç; Vergi; Toplam.
          </div>
        )}
        {loading && !o && <div style={{ ...kart, height: 320, background: C.hair, border: 0 }} aria-busy="true" />}

        {o && (
          <>
            <section style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 160px), 1fr))', gap: 12 }}>
              <Kutucuk etiket="Net satış (KDV hariç)" deger={tlShort(o.net)} alt={donem}
                degisim={o.gecen && o.net / o.gecen.net - 1} kiyas={gecenDonem} />
              <Kutucuk etiket="Sipariş" deger={nf(o.sip)} alt={`${o.kanallar.length} kanal`}
                degisim={o.gecen && o.sip / o.gecen.sip - 1} kiyas={gecenDonem} />
              <Kutucuk etiket="Ortalama sepet" deger={tl(o.net / o.sip)} alt="Net satış / sipariş"
                degisim={o.gecen && (o.net / o.sip) / (o.gecen.net / o.gecen.sip) - 1} kiyas={gecenDonem} />
              <Kutucuk etiket="KDV dahil toplam" deger={tlShort(o.toplam)} alt={donem}
                degisim={o.gecen && o.toplam / o.gecen.toplam - 1} kiyas={gecenDonem} />
            </section>

            <section style={kart}>
              <h2 style={baslik}>Aylık net satış, kanallara göre</h2>
              <p style={not}>
                KDV hariç.{Object.keys(o.kismi).map((a) => ` ${ayYil(a)} yalnız 1–${o.kismi[a]} arası (*).`)}
                {o.eksikAylar.length > 0 && ` ${o.eksikAylar.length === 1 ? ayYil(o.eksikAylar[0]) : `${ayYil(o.eksikAylar[0])} – ${ayYil(o.eksikAylar[o.eksikAylar.length - 1])}`} için yüklenmiş veri yok.`}
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px', fontSize: 12, color: C.muted, marginBottom: 10 }}>
                {o.kanallar.map((k) => (
                  <span key={k.platform}><i style={{ display: 'inline-block', width: 9, height: 9, borderRadius: 2, background: k.renk, marginRight: 6 }} />{k.platform}</span>
                ))}
              </div>
              <div style={{ height: 320 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={o.aylar} margin={{ top: 8, right: 8, left: 4, bottom: 0 }} barCategoryGap="24%">
                    <CartesianGrid vertical={false} stroke={C.hair} />
                    <XAxis dataKey="ay" tickFormatter={ayEtiket} tick={{ fontSize: 12, fill: C.muted }} axisLine={{ stroke: C.line }} tickLine={false} />
                    <YAxis tickFormatter={tlShort} tick={{ fontSize: 12, fill: C.muted }} axisLine={false} tickLine={false} width={64} />
                    <Tooltip cursor={{ fill: C.accentSoft, opacity: 0.6 }} content={<AylikIpucu kanallar={o.kanallar} kismi={o.kismi} />} />
                    {o.kanallar.map((k, i) => (
                      <Bar key={k.platform} dataKey={k.platform} stackId="a" fill={k.renk}
                        radius={i === o.kanallar.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]} isAnimationActive={false} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </section>

            <section style={kart}>
              <h2 style={baslik}>Her kanalın aylık seyri</h2>
              <p style={not}>Aylık net satış, her kanal kendi ölçeğinde (büyüklük için yukarıdaki grafiğe ve tabloya bakın).</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 240px), 1fr))', gap: 16 }}>
                {o.kanallar.map((k) => (
                  <div key={k.platform} style={{ minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                      <b style={{ fontWeight: 600 }}>{k.platform}</b>
                      <span style={{ color: C.muted, fontVariantNumeric: 'tabular-nums' }}>{tlShort(k.net_satis)}</span>
                    </div>
                    <div style={{ height: 96 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={o.aylar} margin={{ top: 4, right: 0, left: 0, bottom: 0 }} barCategoryGap="20%">
                          <XAxis dataKey="ay" tickFormatter={ayEtiket} interval="preserveStartEnd" tick={{ fontSize: 11, fill: C.faint }} axisLine={{ stroke: C.line }} tickLine={false} />
                          <YAxis hide domain={[0, 'dataMax']} />
                          <Tooltip cursor={{ fill: C.accentSoft, opacity: 0.6 }}
                            formatter={(v) => [tl(v), k.platform]} labelFormatter={(a) => `${AY_TAM[+a.slice(5, 7) - 1]}${o.kismi[a] ? ` (1–${o.kismi[a]})` : ''}`}
                            contentStyle={{ fontSize: 12, borderRadius: 10, border: `1px solid ${C.line}` }} />
                          <Bar dataKey={k.platform} fill={k.renk} radius={[3, 3, 0, 0]} isAnimationActive={false} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section style={kart}>
              <h2 style={baslik}>Kanal özeti</h2>
              <p style={not}>{donem}.{o.gecen && ` Geçen yıla göre: ${gunEtiket(o.gecen.bas)} – ${gunEtiket(o.gecen.bit)} ${o.gecen.yil} ile kıyas; "yeni" o dönemde ${MIN_KIYAS_GUN} günden az satışı olan kanal.`} Aktif gün: satış olan gün sayısı. Başlığa tıklayınca sıralanır.</p>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>
                  <thead>
                    <tr>
                      {sutunlar.map((s) => (
                        <th key={s.k} aria-sort={sirala.k === s.k ? (sirala.yon > 0 ? 'ascending' : 'descending') : 'none'}
                          style={{ textAlign: s.sol ? 'left' : 'right', padding: '8px 10px', borderBottom: `1px solid ${C.line}`, fontSize: 12, fontWeight: 600, color: C.muted, whiteSpace: 'nowrap' }}>
                          <button type="button"
                            onClick={() => setSirala((p) => (p.k === s.k ? { k: s.k, yon: -p.yon } : { k: s.k, yon: s.k === 'platform' || s.k === 'ilk' ? 1 : -1 }))}
                            style={{ all: 'unset', cursor: 'pointer', color: sirala.k === s.k ? C.ink : C.muted }}>
                            {s.ad}{sirala.k === s.k ? (sirala.yon > 0 ? ' ↑' : ' ↓') : ''}
                          </button>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sirali.map((k) => (
                      <tr key={k.platform}>
                        {sutunlar.map((s) => (
                          <td key={s.k} style={{ textAlign: s.sol ? 'left' : 'right', padding: '9px 10px', borderBottom: `1px solid ${C.hair}`, whiteSpace: 'nowrap', color: C.ink2 }}>
                            {s.k === 'platform' ? (
                              <span style={{ color: C.ink, fontWeight: 500 }}><i style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: k.renk, marginRight: 8 }} />{k.platform}</span>
                            ) : s.k === 'pay' ? (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>
                                <i style={{ display: 'inline-block', height: 6, borderRadius: 3, background: C.accent, opacity: 0.7, width: `${(k.pay / o.kanallar[0].pay) * 64}px` }} />
                                {pct(k.pay)}
                              </span>
                            ) : s.yoy ? (
                              k.yoy == null
                                ? <span style={{ color: C.faint }}>yeni</span>
                                : <span style={{ color: k.yoy >= 0 ? C.up : C.down, fontWeight: 500 }}>{pctIsaret(k.yoy)}</span>
                            ) : s.f(k[s.k])}
                          </td>
                        ))}
                      </tr>
                    ))}
                    <tr>
                      <td style={{ padding: '9px 10px', fontWeight: 600 }}>Toplam</td>
                      <td style={{ padding: '9px 10px', textAlign: 'right', fontWeight: 600 }}>{tl(o.net)}</td>
                      {o.gecen && (
                        <td style={{ padding: '9px 10px', textAlign: 'right', fontWeight: 600, color: o.net >= o.gecen.net ? C.up : C.down }}>{pctIsaret(o.net / o.gecen.net - 1)}</td>
                      )}
                      <td style={{ padding: '9px 10px', textAlign: 'right' }}>%100</td>
                      <td style={{ padding: '9px 10px', textAlign: 'right', fontWeight: 600 }}>{nf(o.sip)}</td>
                      <td style={{ padding: '9px 10px', textAlign: 'right' }}>%100</td>
                      <td style={{ padding: '9px 10px', textAlign: 'right', fontWeight: 600 }}>{tl(o.net / o.sip)}</td>
                      <td colSpan={2} />
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
}
